<template>
  <div class="p-4">
    <h2>Synchronisation base de données</h2>
    <p class="text-muted">
      Copie de la structure et/ou données de la base <strong>production</strong>
      vers la base de l'environnement courant <strong>{{ envName }}</strong>.
    </p>

    <b-alert variant="danger" show v-if="error">{{ error }}</b-alert>

    <!-- Infos bases -->
    <b-row v-if="prod_db || env_db" class="mb-3">
      <b-col>
        <b-badge variant="secondary">Prod : {{ prod_db }}</b-badge>
        &nbsp;→&nbsp;
        <b-badge variant="primary">Env ({{ envName }}) : {{ env_db }}</b-badge>
      </b-col>
    </b-row>

    <!-- Chargement -->
    <div v-if="loading" class="text-center my-4">
      <fade-loader></fade-loader>
    </div>

    <!-- Tableau de sélection des tables -->
    <div v-if="!loading && tables.length > 0">
      <b-row class="mb-2 align-items-center">
        <b-col>
          <b-form-checkbox v-model="selectAll" @change="toggleAll">
            Tout sélectionner
          </b-form-checkbox>
        </b-col>
        <b-col class="text-right">
          <b-button
            variant="primary"
            :disabled="selectedCount === 0 || copying"
            @click="doCopy"
          >
            <span v-if="copying"><b-spinner small></b-spinner> Copie en cours…</span>
            <span v-else>Copier {{ selectedCount > 0 ? '(' + selectedCount + ' tables)' : '' }}</span>
          </b-button>
        </b-col>
      </b-row>

      <b-table-simple bordered small responsive>
        <b-thead>
          <b-tr>
            <b-th style="width:40px"></b-th>
            <b-th>Table</b-th>
            <b-th>Dans <code>{{ env_db }}</code></b-th>
            <b-th>Données</b-th>
            <b-th style="width:120px">Nb lignes max</b-th>
            <b-th>Résultat</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr v-for="t in tables" :key="t.name" :variant="rowVariant(t)">
            <b-td class="text-center">
              <b-form-checkbox v-model="t.selected" @change="updateSelectAll"></b-form-checkbox>
            </b-td>
            <b-td><code>{{ t.name }}</code></b-td>
            <b-td class="text-center">
              <span v-if="t.in_env && t.result === 'ok'" class="text-success" title="Table présente dans l'env">✓ oui</span>
              <span v-else-if="t.in_env" class="text-warning" title="Existait déjà, sera écrasée">⚠ oui</span>
              <span v-else class="text-muted">non</span>
            </b-td>
            <b-td>
              <b-form-select
                v-model="t.copy_data"
                :disabled="!t.selected"
                size="sm"
                :options="[
                  { value: 'none', text: 'Aucune donnée' },
                  { value: 'all', text: 'Toutes les données' },
                  { value: 'limit', text: 'Premières lignes' }
                ]"
              ></b-form-select>
            </b-td>
            <b-td>
              <b-form-input
                v-model.number="t.data_limit"
                type="number"
                min="1"
                size="sm"
                :disabled="!t.selected || t.copy_data !== 'limit'"
              ></b-form-input>
            </b-td>
            <b-td>
              <span v-if="t.result === 'loading'"><b-spinner small></b-spinner> En cours…</span>
              <span v-else-if="t.result === 'ok'" class="text-success font-weight-bold">
                ✓ OK
                <template v-if="t.result_steps">
                  <small class="text-muted font-weight-normal ml-1">
                    <span v-for="s in t.result_steps" :key="s.step">
                      <span v-if="s.step === 'copy_data'"> · {{ s.rows }} ligne{{ s.rows !== 1 ? 's' : '' }}</span>
                      <span v-if="s.step === 'copy_triggers'">
                        <span v-if="s.status === 'partial'" class="text-warning"> · ⚠ {{ s.count }}/{{ s.count + (s.errors ? s.errors.length : 0) }} triggers</span>
                        <span v-else> · {{ s.count }} trigger{{ s.count !== 1 ? 's' : '' }}</span>
                      </span>
                    </span>
                  </small>
                </template>
              </span>
              <span v-else-if="t.result === 'error'" class="text-danger" :title="t.result_detail">✗ Erreur</span>
              <span v-else class="text-muted">—</span>
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </div>

    <div v-if="!loading && tables.length === 0 && !error" class="text-muted">
      Aucune table trouvée dans la base de production.
    </div>

    <!-- Récapitulatif des erreurs -->
    <div v-if="copyErrors.length > 0" class="mt-3">
      <b-alert variant="danger" show>
        <strong>Erreurs lors de la copie :</strong>
        <ul class="mb-0">
          <li v-for="e in copyErrors" :key="e.table">
            <code>{{ e.table }}</code> : {{ e.error }}
          </li>
        </ul>
      </b-alert>
    </div>
  </div>
</template>

<script>
import FadeLoader from "vue-spinner/src/FadeLoader.vue";
import database_service from "../services/database.js";
import store from "../store";

export default {
  name: "DatabaseSync",
  components: { FadeLoader },

  data() {
    return {
      loading: true,
      copying: false,
      error: null,
      prod_db: null,
      env_db: null,
      tables: [],
      selectAll: false,
      copyErrors: []
    };
  },

  computed: {
    env() {
      return store.state.user && store.state.user.env;
    },
    envName() {
      const uid = this.env;
      if (!uid) return uid;
      const client = store.state.client;
      const envDetails = client && client.details && client.details.env && client.details.env[uid];
      return (envDetails && envDetails.name) ? envDetails.name : uid;
    },
    selectedCount() {
      return this.tables.filter(t => t.selected).length;
    }
  },

  async mounted() {
    if (
        store.state.user.rules &&
        store.state.user.rules.database == "désactivé"
    ) {
        this.$router.push({ name: "profile" });
    }

    if (
        store.state.user.rules &&
        store.state.user.rules.database == "certaines tables"
    ) {
        this.$router.push({ name: "profile" });
    }


    if (!this.env) {
      this.error = "Vous n'êtes pas sur un environnement. Cette page est uniquement disponible avec un environnement actif.";
      this.loading = false;
      return;
    }
    await this.loadTables();
  },

  methods: {
    async loadTables() {
      this.loading = true;
      this.error = null;
      try {
        const res = await database_service.getSyncTables();
        if (res.errors) {
          this.error = Array.isArray(res.errors) ? res.errors.join(', ') : String(res.errors);
          return;
        }
        this.prod_db = res.prod_db;
        this.env_db = res.env_db;

        const envSet = new Set(res.env_tables || []);
        this.tables = (res.prod_tables || []).map(name => ({
          name,
          in_env: envSet.has(name),
          selected: false,
          copy_structure: true,
          copy_data: 'all',
          data_limit: 100,
          result: null,
          result_detail: null,
          result_steps: null
        }));
      } catch (err) {
        this.error = err.response?.data?.msg || err.message || String(err);
      } finally {
        this.loading = false;
      }
    },

    toggleAll(val) {
      this.tables.forEach(t => (t.selected = val));
    },

    updateSelectAll() {
      this.selectAll = this.tables.length > 0 && this.tables.every(t => t.selected);
    },

    rowVariant(t) {
      if (t.result === 'loading') return 'warning';
      if (t.result === 'ok') return 'success';
      if (t.result === 'error') return 'danger';
      return '';
    },

    async doCopy() {
      this.copying = true;
      this.copyErrors = [];

      // Réinitialise les résultats des tables sélectionnées
      this.tables.forEach(t => {
        if (t.selected) {
          t.result = null;
          t.result_detail = null;
          t.result_steps = null;
        }
      });

      const selectedTables = this.tables.filter(t => t.selected);

      // Envoi table par table pour éviter les timeouts sur les grosses tables
      for (const t of selectedTables) {
        this.$set(t, 'result', 'loading');
        try {
          const res = await database_service.copyTablesFromProd([{
            table: t.name,
            copy_structure: t.copy_structure,
            copy_data: t.copy_data,
            data_limit: t.data_limit
          }]);
          if (!res.results) throw new Error(res.msg || 'Erreur inconnue');
          const r = res.results[0];
          this.$set(t, 'result', r.status);
          this.$set(t, 'result_steps', r.steps || []);
          if (r.status === 'ok' && t.copy_structure) {
            this.$set(t, 'in_env', true);
          }
          if (r.status === 'error') {
            this.$set(t, 'result_detail', r.error);
            this.copyErrors.push({ table: r.table, error: r.error });
          }
        } catch (err) {
          this.$set(t, 'result', 'error');
          const msg = err.response?.data?.msg || err.message || String(err);
          this.$set(t, 'result_detail', msg);
          this.copyErrors.push({ table: t.name, error: msg });
        }
      }

      this.copying = false;
    }
  }
};
</script>
